import React from 'react';
import { ClipboardCheck, Check, Circle } from 'lucide-react';
import type { MockFlowChecklist } from '@/types/purchaseFlow';

const CHECKLIST_LABELS: Record<string, string> = {
  vendor_selected:  'Vendor selected',
  items_confirmed:  'Items confirmed',
  po_created:       'PO created',
  delivery_set:     'Delivery scheduled',
  iqc_rules_set:    'IQC rules set',
  goods_received:   'Goods received',
  iqc_passed:       'IQC passed',
  invoice_created:  'Invoice created',
  amounts_matched:  'Amounts matched',
  payment_complete: 'Payment complete',
};

interface FlowChecklistBlockProps {
  data: unknown;
  onAction: (message: string) => void;
}

const FlowChecklistBlock: React.FC<FlowChecklistBlockProps> = ({ data, onAction }) => {
  const checklist = data as MockFlowChecklist;
  if (!checklist?.checklist) return null;

  const allPassed = Object.values(checklist.checklist).every(Boolean);

  return (
    <div className={`mt-2 rounded-lg border p-3 text-xs ${
      allPassed
        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
        : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
    }`}>
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck className={`w-4 h-4 ${allPassed ? 'text-emerald-600' : 'text-amber-600'}`} />
        <span className={`font-semibold ${allPassed ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}`}>
          Flow Summary — {checklist.po_number}
        </span>
      </div>

      <div className="space-y-1.5 mb-3">
        {Object.entries(checklist.checklist).map(([key, passed]) => (
          <div key={key} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            {passed
              ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              : <Circle className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
            }
            <span className={passed ? '' : 'text-gray-400'}>{CHECKLIST_LABELS[key] || key}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-current/10 pt-2 space-y-1 text-gray-600 dark:text-gray-400">
        <div className="flex justify-between">
          <span>PO Total</span>
          <span className="font-mono">{checklist.currency} {checklist.totals.po_total.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span>Received</span>
          <span className="font-mono">{checklist.currency} {checklist.totals.received_total.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span>Invoiced</span>
          <span className="font-mono">{checklist.currency} {checklist.totals.invoiced_total.toLocaleString()}</span>
        </div>
      </div>

      {allPassed && (
        <button
          onClick={() => onAction('Close flow')}
          className="mt-3 w-full py-1.5 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        >
          Close Flow
        </button>
      )}
    </div>
  );
};

export default FlowChecklistBlock;
